import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Sparkles, ExternalLink } from 'lucide-react';

import { useTranslation } from 'react-i18next';

import { homeTarget } from '@/components/ShareHomeButton';
/**
 * SharedDYKPage — public SPA route for shared Did You Know facts.
 *
 * Path: /share/dyk/:shareId
 *
 * Fetches the public payload from /api/did-you-know/share/{share_id}
 * and renders it in a clean card. Open-graph metadata + the canonical
 * preview image are served by the backend OG route the share URL
 * actually points at (/api/og/share/dyk_xxx) — crawlers hit that, then
 * 302 humans here to this React route.
 */
export default function SharedDYKPage() {
  const {
    t
  } = useTranslation();

  const { shareId } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${window.location.origin}/api/did-you-know/share/${shareId}`)
      .then((r) => { if (alive) setDoc(r.data); })
      .catch(() => { if (alive) setError('This shared fact is unavailable.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [shareId]);

  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center" style={{ background: '#0f172a' }} data-testid="shared-dyk-loading">
        <div className="w-8 h-8 border-2 border-t-transparent border-purple-400 rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !doc) {
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center text-center p-4" style={{ background: '#0f172a', color: '#f1f5f9' }} data-testid="shared-dyk-error">
        <p className="text-lg font-semibold mb-2">{t('generated.shareddykpage_fact_unavailable_985f')}</p>
        <p className="text-sm opacity-70 mb-6">{error || 'This link may have expired.'}</p>
        <button
          onClick={() => navigate(homeTarget(), { replace: true })}
          className="px-4 py-2 rounded-full text-sm font-medium"
          style={{ background: '#6366f1', color: '#fff' }}
        >
          {t('generated.shareddykpage_open_eili_3a5b')}
        </button>
      </div>
    );
  }

  const handleOpenInApp = () => {
    // Sends the user into the in-app DYK page; if not logged in,
    // the protected route will redirect to /login.
    navigate(`/did-you-know?fact=${doc.fact_id}`);
  };

  return (
    <div className="min-h-dvh px-4 py-10" style={{ background: '#0f172a', color: '#f1f5f9' }} data-testid="shared-dyk-page">
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => navigate(homeTarget(), { replace: true })}
          className="inline-flex items-center gap-2 text-sm opacity-75 mb-6 hover:opacity-100"
          data-testid="shared-dyk-back"
        >
          <ArrowLeft className="h-4 w-4" /> {t('generated.shareddykpage_back_c224')}
        </button>

        <div
          className="rounded-3xl p-8 sm:p-10 shadow-2xl"
          style={{
            background: 'linear-gradient(180deg, rgba(30,41,59,0.85) 0%, rgba(15,23,42,0.85) 100%)',
            border: '1px solid rgba(99,102,241,0.25)',
            backdropFilter: 'blur(8px)',
          }}
        >
          {/* Badge */}
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold tracking-wider mb-6"
               style={{ background: 'rgba(167,139,250,0.15)', color: '#c4b5fd' }}>
            <Sparkles className="h-3.5 w-3.5" />
            {t('generated.shareddykpage_did_you_know_e12f')}
          </div>

          {/* Title */}
          <h1 className="text-2xl sm:text-3xl font-bold leading-tight mb-5" data-testid="shared-dyk-title">
            {doc.title}
          </h1>

          {/* Body */}
          <p className="text-base leading-relaxed opacity-90 whitespace-pre-line" data-testid="shared-dyk-body">
            {doc.content}
          </p>

          {/* Footer CTA */}
          <div className="mt-8 pt-6 flex items-center justify-between" style={{ borderTop: '1px solid rgba(148,163,184,0.15)' }}>
            <span className="text-sm italic opacity-70">{t('generated.shareddykpage_eili_e4a0')}</span>
            <button
              onClick={handleOpenInApp}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors hover:scale-[1.02]"
              style={{ background: '#6366f1', color: '#fff' }}
              data-testid="shared-dyk-open-in-app"
            >
              {t('generated.shareddykpage_open_in_eili_3b96')} <ExternalLink className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
